import {ChartDataset, Scale, ScriptableContext, ScriptableLineSegmentContext, Tick, TooltipItem} from 'chart.js';
import moment from 'moment';
// webpack strips the locales of moment (IgnorePlugin in vue.config.js), the months of the time axis need the czech one
import 'moment/locale/cs';

/**
 * The chart of the price history of a listing as PHP prepares it (PriceHistoryChartData): the figures are computed
 * there once for this chart and for the picture of the documents, the chart only draws them.
 */
export interface PriceChange {
    kind: 'rise' | 'drop';
    absolute: number;
    relative: number;
    previousDate: string;
}

export interface PriceHistoryPoint {
    date: string;
    price: number;
    event: 'listed' | 'removed' | null;
    change: PriceChange | null;
}

export interface PurchaseContractPoint {
    date: string;
    price: number;
    /** null when the listing had no price on the day of the signature */
    absolute: number | null;
    relative: number | null;
}

export interface PriceRange {
    date: string;
    lowest: number;
    highest: number;
}

export interface PriceHistorySummary {
    listedPrice: number;
    listedDate: string;
    lastPrice: number;
    relativeChange: number;
    lowestPrice: number;
    highestPrice: number;
    daysOnOffer: number;
}

/** The items of the legend the chart shows, in their order (PriceHistoryLegendItem) */
export type LegendKey = 'price' | 'check' | 'transition' | 'rise' | 'drop' | 'range' | 'listed' | 'removed' | 'stillAdvertised' | 'purchaseContract';

export interface PriceHistoryChartData {
    currency: string;
    points: PriceHistoryPoint[];
    purchaseContract: PurchaseContractPoint | null;
    stillAdvertised: boolean;
    thinLine: boolean;
    markedChanges: boolean;
    labelledChanges: boolean;
    extreme: boolean;
    ranges: PriceRange[];
    summary: PriceHistorySummary;
    eventDates: string[];
    today: string;
    legend: LegendKey[];
}

/** What a point of a dataset carries for the tooltip and the styles */
export interface PriceHistoryRaw {
    x: string;
    y: number;
    point?: PriceHistoryPoint;
    contract?: PurchaseContractPoint;
    range?: PriceRange;
}

export const COLORS = {
    line: '#509bfe',
    listed: '#493bca',
    removed: '#ea282e',
    contract: '#f0a500',
    rise: '#28b24d',
    drop: '#ea282e',
    grid: 'rgba(0, 0, 0, 0.06)',
    muted: '#6c757d',
};

/** Over this many points the dots of the line are smaller */
const MANY_POINTS = 40;

/** The time axis of a single day spans this many days around it */
const SINGLE_DAY_SPAN_DAYS = 7;

/** A dataset which the tooltip leaves out */
export type PriceHistoryDataset = ChartDataset<'line', PriceHistoryRaw[]> & {tooltipSkip?: boolean};

export function money(value: number, currency: string): string {
    return new Intl.NumberFormat('cs', {style: 'currency', currency, maximumFractionDigits: 0}).format(value);
}

export function signedMoney(value: number, currency: string): string {
    return new Intl.NumberFormat('cs', {style: 'currency', currency, maximumFractionDigits: 0, signDisplay: 'exceptZero'}).format(value);
}

export function percent(share: number): string {
    return new Intl.NumberFormat('cs', {style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero'}).format(share);
}

export function date(value: string): string {
    return moment(value).format('D. M. YYYY');
}

/**
 * "2,5 mil. Kč", "900 tis. Kč"; steps below 100 thousand among millions get two decimals, so that the labels of the
 * ticks differ (the same labels as the picture of the documents, PriceAxis)
 */
export function axisMoney(value: number, step = 100_000): string {
    if (Math.abs(value) >= 1e6) {
        return (value / 1e6).toLocaleString('cs', {maximumFractionDigits: step < 100_000 ? 2 : 1}) + ' mil. Kč';
    }

    return (value / 1e3).toLocaleString('cs', {maximumFractionDigits: 0}) + ' tis. Kč';
}

/** "24,7 – 29,7 mil. Kč" */
export function rangeText(lowest: number, highest: number, currency: string): string {
    if (lowest === highest) {
        return money(lowest, currency);
    }

    return axisMoney(lowest).replace(' mil. Kč', '').replace(' tis. Kč', '') + ' – ' + axisMoney(highest);
}

/** "1 den", "3 dny", "113 dní" */
export function days(count: number): string {
    const unit = count === 1 ? 'den' : (count >= 2 && count <= 4 ? 'dny' : 'dní');

    return count.toLocaleString('cs') + ' ' + unit;
}

/** The price is shown with some space above and below, as in the picture of the documents (PriceAxis) */
export function priceBounds(data: PriceHistoryChartData): {min: number; max: number} {
    const prices = data.points.map(point => point.price);

    if (data.purchaseContract !== null) {
        prices.push(data.purchaseContract.price);
    }

    const lowest = Math.min(...prices);
    const highest = Math.max(...prices);
    const padding = Math.max((highest - lowest) * 0.15, highest * 0.03);

    return {min: lowest - padding, max: highest + padding};
}

/** The time axis shows only dates, never hours, also when the listing lasted a day or two */
export const TIME_AXIS = {
    minUnit: 'day' as const,
    tooltipFormat: 'D. M. YYYY',
    displayFormats: {day: 'D. M.', week: 'D. M.', month: 'MMM YYYY', quarter: 'MMM YYYY', year: 'YYYY'},
};

/** The closest the positions of two labelled dates can be, the slanted labels would overlap (EventDateTicks) */
const MINIMUM_TICK_DISTANCE = 16;

/**
 * The dates of the events labelled on the time axis, as in the picture of the documents (EventDateTicks): a date too
 * close to the next one would overlap its label and is left out; the first and the last date always stay.
 *
 * @param dates sorted
 * @param position the position of a date on the axis
 */
export function eventDateTicks(dates: string[], position: (date: string) => number, minimumDistance: number): string[] {
    // From the last date back, so that the latest events (the removal, the contract) keep their labels
    const kept: string[] = [];
    const reversed = [...dates].reverse();
    const first = reversed.length - 1;

    reversed.forEach((date, index) => {
        const tooClose = kept.length > 0 && position(kept[kept.length - 1]) - position(date) < minimumDistance;

        if (!tooClose) {
            kept.push(date);
        } else if (index === first) {
            // The first date stays instead of the one after it, unless that one is the last date
            if (kept.length > 1) {
                kept.pop();
            }

            kept.push(date);
        }
    });

    return kept.reverse();
}

/**
 * The ticks of the time axis are the dates of the events, so that each label belongs to a point of the line; which of
 * them fit is decided by the width of the axis
 */
export function eventAxisTicks(data: PriceHistoryChartData): (scale: Scale) => void {
    return scale => {
        const span = scale.max - scale.min;
        const position = (value: string) => (span > 0 ? (moment(value).valueOf() - scale.min) / span * scale.width : 0);

        scale.ticks = eventDateTicks(data.eventDates, position, MINIMUM_TICK_DISTANCE).map(value => ({value: moment(value).valueOf()}));
    };
}

/** The labels of the time axis: exact dates with the year, as in the picture of the documents */
export function dateTick(value: number | string, index: number, ticks: Tick[]): string {
    return moment(ticks[index].value).format('DD.MM.YYYY');
}

/** A single day gets an axis of some days around it, Chart.js would not draw a zero span */
export function timeBounds(data: PriceHistoryChartData): {min?: string; max?: string} {
    const dates = [...data.points.map(point => point.date), ...(data.purchaseContract ? [data.purchaseContract.date] : [])];

    if (data.stillAdvertised) {
        dates.push(data.today);
    }

    if (new Set(dates).size > 1) {
        return {};
    }

    return {
        min: moment(dates[0]).subtract(SINGLE_DAY_SPAN_DAYS, 'days').format('YYYY-MM-DD'),
        max: moment(dates[0]).add(SINGLE_DAY_SPAN_DAYS, 'days').format('YYYY-MM-DD'),
    };
}

/** A point which repeats the price, kept to show the listing was watched */
export function isCheck(point: PriceHistoryPoint | undefined): boolean {
    return point !== undefined && point.event === null && point.change === null;
}

/** The change marked on the point, null for none or when the changes are not marked */
export function markedChange(data: PriceHistoryChartData, point: PriceHistoryPoint | undefined): 'rise' | 'drop' | null {
    if (!data.markedChanges || point === undefined || point.event !== null || point.change === null) {
        return null;
    }

    return point.change.kind;
}

function pointOf(context: ScriptableContext<'line'>): PriceHistoryPoint | undefined {
    return (context.raw as PriceHistoryRaw | undefined)?.point;
}

function eventColor(point: PriceHistoryPoint | undefined): string | null {
    if (point?.event === 'listed') {
        return COLORS.listed;
    }

    return point?.event === 'removed' ? COLORS.removed : null;
}

/**
 * The datasets of the chart: the line of the prices (the range of the prices of an extreme with the events as
 * markers), the dotted line until today of an advertised listing and the purchase contract.
 */
export function datasets(data: PriceHistoryChartData, areaGradient: (context: ScriptableContext<'line'>) => CanvasGradient | string): PriceHistoryDataset[] {
    const smallDots = data.points.length > MANY_POINTS;
    const thin = data.thinLine;
    const raws: PriceHistoryRaw[] = data.points.map(point => ({x: point.date, y: point.price, point}));
    const result: PriceHistoryDataset[] = [];

    const pointStyle = (context: ScriptableContext<'line'>) => {
        const point = pointOf(context);

        return point?.event === 'removed' ? 'crossRot' : (markedChange(data, point) ? 'triangle' : 'circle');
    };
    const pointRadius = (context: ScriptableContext<'line'>) => {
        const point = pointOf(context);

        if (point?.event) {
            return 6;
        }

        // The other points are dots, the checks of the price show the listing was watched
        return markedChange(data, point) ? 5.5 : (smallDots ? 2 : 3);
    };
    const pointColor = (context: ScriptableContext<'line'>) => {
        const point = pointOf(context);
        const change = markedChange(data, point);

        return eventColor(point) ?? (change === 'rise' ? COLORS.rise : (change === 'drop' ? COLORS.drop : COLORS.line));
    };
    const emphasised = (context: ScriptableContext<'line'>) => {
        const point = pointOf(context);

        return point !== undefined && (point.event !== null || markedChange(data, point) !== null);
    };

    if (!data.extreme) {
        // The price is known only on the dates of the records: solid where it held, a dashed slope where it
        // changed, as the change happened some time between the two records
        const changed = (context: ScriptableLineSegmentContext) => data.markedChanges
            && (raws[context.p1DataIndex]?.point?.change ?? null) !== null;

        result.push({
            label: 'Cena v inzerátu',
            data: raws,
            segment: {
                borderDash: context => (changed(context) ? [5, 5] : undefined),
                borderColor: context => (changed(context) ? COLORS.line + 'bf' : undefined),
            },
            borderColor: COLORS.line,
            borderWidth: thin ? 1.75 : 2.5,
            fill: thin ? false : 'start',
            backgroundColor: areaGradient,
            pointStyle,
            pointRadius,
            pointHoverRadius: context => Math.max(pointRadius(context), 5),
            pointRotation: context => (markedChange(data, pointOf(context)) === 'drop' ? 180 : 0),
            pointBackgroundColor: pointColor,
            pointBorderColor: context => (pointOf(context)?.event === 'removed' ? COLORS.removed : (emphasised(context) ? '#fff' : COLORS.line)),
            pointBorderWidth: context => (pointOf(context)?.event === 'removed' ? 3 : (emphasised(context) ? 1.5 : 0)),
            order: 2,
        });
    } else {
        const edge = (label: string, key: 'lowest' | 'highest', fill: false | '-1', tooltipSkip: boolean): PriceHistoryDataset => ({
            label,
            data: data.ranges.map(range => ({x: range.date, y: range[key], range})),
            borderColor: COLORS.line,
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 0,
            fill,
            backgroundColor: 'rgba(80, 155, 254, 0.18)',
            tooltipSkip,
            order: 3,
        });

        result.push(edge('Nejnižší cena', 'lowest', false, true), edge('Nejvyšší cena', 'highest', '-1', false));
        result.push({
            label: 'Události',
            data: raws.filter(raw => raw.point?.event),
            showLine: false,
            pointStyle,
            pointRadius: 6,
            pointHoverRadius: 7,
            pointBackgroundColor: pointColor,
            pointBorderColor: context => (pointOf(context)?.event === 'removed' ? COLORS.removed : '#fff'),
            pointBorderWidth: context => (pointOf(context)?.event === 'removed' ? 3 : 2),
            order: 1,
        });
    }

    if (data.stillAdvertised) {
        const last = data.points[data.points.length - 1];

        result.push({
            label: 'Stále v nabídce',
            data: [{x: last.date, y: last.price}, {x: data.today, y: last.price}],
            borderColor: COLORS.line,
            borderDash: [2, 4],
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            fill: false,
            tooltipSkip: true,
            order: 3,
        });
    }

    if (data.purchaseContract !== null) {
        const contract = data.purchaseContract;

        result.push({
            label: 'Kupní smlouva',
            data: [{x: contract.date, y: contract.price, contract}],
            showLine: false,
            pointStyle: 'rectRot',
            pointRadius: 8,
            pointHoverRadius: 9,
            pointBackgroundColor: COLORS.contract,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            order: 1,
        });
    }

    return result;
}

/** The labels of the price axis: the step of the ticks decides the decimals */
export function priceTick(value: number | string, index: number, ticks: Tick[]): string {
    const step = ticks.length > 1 ? Math.abs(ticks[1].value - ticks[0].value) : 100_000;

    return axisMoney(Number(value), step);
}

/** The dotted line until today and the lower edge of the range have no tooltip of their own */
export function tooltipShows(item: TooltipItem<'line'>): boolean {
    return !('tooltipSkip' in item.dataset && item.dataset.tooltipSkip === true);
}

export function tooltipTitle(items: TooltipItem<'line'>[]): string {
    if (items.length === 0) {
        return '';
    }

    const raw = items[0].raw as PriceHistoryRaw;

    if (raw.range) {
        return 'Kolem ' + date(raw.x);
    }

    return date(raw.x);
}

export function tooltipLabel(item: TooltipItem<'line'>, currency: string): string {
    const raw = item.raw as PriceHistoryRaw;

    if (raw.range) {
        return (raw.range.lowest === raw.range.highest ? 'Cena: ' : 'Ceny: ') + rangeText(raw.range.lowest, raw.range.highest, currency);
    }

    if (raw.contract) {
        return 'Kupní smlouva: ' + money(raw.y, currency);
    }

    let prefix = 'Cena: ';

    if (raw.point?.event === 'listed') {
        prefix = 'Vložení inzerátu: ';
    } else if (raw.point?.event === 'removed') {
        prefix = 'Inzerát odstraněn: ';
    } else if (isCheck(raw.point)) {
        prefix = 'Cena beze změny: ';
    }

    return prefix + money(raw.y, currency);
}

export function tooltipFooter(items: TooltipItem<'line'>[], currency: string): string[] {
    if (items.length === 0) {
        return [];
    }

    const raw = items[0].raw as PriceHistoryRaw;

    if (raw.contract) {
        if (raw.contract.absolute === null || raw.contract.relative === null || raw.contract.absolute === 0) {
            return [];
        }

        return [signedMoney(raw.contract.absolute, currency) + ' (' + percent(raw.contract.relative) + ') proti ceně v inzerátu ke dni podpisu'];
    }

    const change = raw.point?.change;

    if (!change) {
        return [];
    }

    return [
        signedMoney(change.absolute, currency) + ' (' + percent(change.relative) + ') proti předchozí ceně',
        'Změna mezi ' + date(change.previousDate) + ' a ' + date(raw.x),
    ];
}

export interface SummaryItem {
    label: string;
    value: string;
    note: string;
}

export function summaryItems(data: PriceHistoryChartData): SummaryItem[] {
    const summary = data.summary;
    const items: SummaryItem[] = [
        {label: 'Vložení inzerátu', value: money(summary.listedPrice, data.currency), note: date(summary.listedDate)},
        data.extreme
            ? {label: 'Rozpětí cen', value: rangeText(summary.lowestPrice, summary.highestPrice, data.currency), note: 'ceny více inzerátů'}
            : {
                label: data.stillAdvertised ? 'Aktuální cena' : 'Poslední cena',
                value: money(summary.lastPrice, data.currency),
                note: percent(summary.relativeChange) + ' od vložení',
            },
        {label: 'V nabídce', value: days(summary.daysOnOffer), note: data.stillAdvertised ? 'stále aktivní' : 'inzerát odstraněn'},
    ];

    if (data.purchaseContract !== null) {
        const relative = data.purchaseContract.relative;

        items.push({
            label: 'Kupní smlouva',
            value: money(data.purchaseContract.price, data.currency),
            // The date of the signature in place of "proti inzerátu", the card would be wider than the others;
            // a change rounded to 0 % tells nothing
            note: date(data.purchaseContract.date) + (relative === null || Math.round(relative * 1000) === 0 ? '' : ', ' + percent(relative)),
        });
    }

    return items;
}

export interface LegendItem {
    key: LegendKey;
    label: string;
}

const LEGEND_LABELS: Record<LegendKey, string> = {
    price: 'Cena v inzerátu',
    check: 'Cena ověřena',
    transition: 'Změna ceny',
    rise: 'Zdražení',
    drop: 'Zlevnění',
    range: 'Rozpětí cen',
    listed: 'Vložení inzerátu',
    removed: 'Inzerát odstraněn',
    stillAdvertised: 'Stále v nabídce',
    purchaseContract: 'Kupní smlouva',
};

/** The items of the legend as PHP chose them, only those the chart shows */
export function legendItems(data: PriceHistoryChartData): LegendItem[] {
    return data.legend.map(key => ({key, label: LEGEND_LABELS[key]}));
}

export interface ChangeLabel {
    index: number;
    text: string;
    color: string;
    rise: boolean;
}

/** The percentages next to the markers of the changes, while there are few changes */
export function changeLabels(data: PriceHistoryChartData): ChangeLabel[] {
    if (!data.labelledChanges || data.extreme) {
        return [];
    }

    return data.points.flatMap((point, index) => {
        const change = markedChange(data, point);

        return change === null || point.change === null
            ? []
            : [{index, text: percent(point.change.relative), color: change === 'rise' ? COLORS.rise : COLORS.drop, rise: change === 'rise'}];
    });
}
